import type { PowerProjection } from 'statisticsdb/types';
import { isPreStart } from 'helpers/experiment-status';

/**
 * Every experiment on one time axis: what happened, what is scheduled, and what is only
 * estimated, kept visibly apart.
 *
 * Three sources, in order of trust:
 *
 *  1. experiments.startsAt / endsAt and the first deploy: records of what happened. The
 *     end is the randomising window's end, not endsAt, because a deployed test stopped
 *     being a test when it was deployed.
 *  2. experiment_schedules: what someone asked for. Only a schedule the engine will still
 *     act on counts (armed or active, and not already past).
 *  3. Estimates: a running test's power projection, and a draft's planned length. A
 *     pipeline stage that has not started is placed after the stage before it.
 *
 * Nothing here writes a date anywhere. Rescheduling from the timeline, if it is ever
 * added, must write experiment_schedules, never startsAt / endsAt: three readers treat
 * those as records (see the schedules schema comment).
 */

const DAY_MS = 24 * 60 * 60 * 1000;

export type EdgeKind = 'actual' | 'scheduled' | 'estimated';

/**
 * When the winner was rolled out to everyone, and for how long.
 *
 * Read from the deployed variant's timestamp, not from the status: ending a deployed test
 * sets it to Ended, but the deployed variant row stays, so a test that shipped and was
 * later ended still shows that it shipped. `ongoing` is a deployment still live today,
 * drawn open to the right edge like a running test.
 */
export type Deployment = { at: number; until: number; ongoing: boolean };

export type TimelineBar = {
  id: number;
  name: string;
  status: string;
  start: number;
  startKind: EdgeKind;
  end: number;
  /** 'open' is a running test with no end in sight: drawn fading to the right edge. */
  endKind: EdgeKind | 'open';
  /** Set for experiments in a pipeline: the stage they belong to. */
  stage: string | null;
  /** Written for a reader: why the end is where it is. */
  note: string | null;
  /** Set for any test that was ever deployed, whatever its status is now. */
  deployment: Deployment | null;
};

export type TimelineGroup = { key: string; label: string; bars: TimelineBar[] };

export type Unplaced = { id: number; name: string; reason: string };

export type Timeline = { groups: TimelineGroup[]; unplaced: Unplaced[]; allBars: TimelineBar[] };

/** Structural rather than the full rows, so the builder is testable with plain objects. */
export type TimelineExperiment = {
  id: number;
  name: string;
  status: string;
  startsAt: number | null;
  endsAt: number | null;
  variants?: readonly { deployed: number | null }[] | null;
  schedule?: { state: string; startAt: number | null; stopAt: number | null } | null;
};

export type TimelinePipeline = {
  id: number;
  name: string;
  status: string;
  stages: readonly {
    position: number;
    name: string;
    state: string;
    /**
     * How the engine advances past this stage (advance-pipeline.ts): 'all-concluded'
     * waits for every member, 'any-significant' moves on at the first conclusion,
     * 'manual' never moves on by itself. Optional so older payloads read as the default.
     */
    advanceRule?: string;
    items: readonly { experimentId: number }[];
  }[];
};

type BuildTimelineParams = {
  experiments: readonly TimelineExperiment[];
  pipelines: readonly TimelinePipeline[];
  /** Running experiments' projections, keyed by id. */
  projections: Readonly<Record<string, PowerProjection>>;
  /** Planned length in days for drafts planned in the wizard, keyed by id; absent otherwise. */
  plannedDays: ReadonlyMap<number, number>;
  nowMs: number;
  /**
   * The rows the Library is currently showing. The timeline is built from *every*
   * experiment, because a pipeline stage's dates depend on all of its members whether
   * or not a filter hides them, and only then narrowed to these for drawing.
   */
  visibleIds?: ReadonlySet<number>;
};

const LIVE_SCHEDULE_STATES = new Set(['armed', 'active']);

/** A schedule the engine will still act on, or null for either end. */
export const pendingSchedule = (experiment: TimelineExperiment, nowMs: number) => {
  const schedule = experiment.schedule;
  if (!schedule || !LIVE_SCHEDULE_STATES.has(schedule.state)) return { startAt: null, stopAt: null };
  return {
    startAt: schedule.startAt !== null && schedule.startAt > nowMs ? schedule.startAt : null,
    stopAt: schedule.stopAt !== null && schedule.stopAt > nowMs ? schedule.stopAt : null,
  };
};

/**
 * Whether a draft will be drawn with a planned length, so its plan is worth fetching.
 * The same conditions place() uses, so the two cannot disagree: a draft fetched for and
 * never drawn is a wasted request, and one drawn but never fetched reads as "no traffic
 * history" when the truth is that nobody asked.
 */
type NeedsPlannedLengthParams = {
  experiment: TimelineExperiment;
  queuedIds: ReadonlySet<number>;
  nowMs: number;
};

export const needsPlannedLength = ({ experiment, queuedIds, nowMs }: NeedsPlannedLengthParams): boolean => {
  if (!isPreStart(experiment.status)) return false;
  const { startAt, stopAt } = pendingSchedule(experiment, nowMs);
  if (startAt !== null && stopAt !== null && stopAt > startAt) return false;
  if (experiment.schedule?.state === 'held') return true;
  return startAt !== null || queuedIds.has(experiment.id);
};

const firstDeploy = (experiment: TimelineExperiment): number | null => {
  const deploys = (experiment.variants ?? []).map((v) => v.deployed).filter((d): d is number => d !== null);
  return deploys.length ? Math.min(...deploys) : null;
};

/**
 * A deployed test's rollout. Deployed is still live; Ended stopped at endsAt. An endsAt
 * recorded before the deploy never draws a span running backwards: it is a marker only.
 */
const deploymentOf = (experiment: TimelineExperiment, nowMs: number): Deployment | null => {
  const at = firstDeploy(experiment);
  if (at === null) return null;
  if (experiment.status === 'Deployed') return { at, until: Math.max(at, nowMs), ongoing: true };
  return { at, until: Math.max(at, experiment.endsAt ?? at), ongoing: false };
};

type Placement = { bar: TimelineBar } | { unplaced: string } | null;

type PlaceParams = {
  experiment: TimelineExperiment;
  stage: string | null;
  /** When the stage before this one is expected to finish, for a pending pipeline stage. */
  stageStart: number | null;
  params: BuildTimelineParams;
};

function place({ experiment, stage, stageStart, params }: PlaceParams): Placement {
  const { nowMs, projections, plannedDays } = params;
  const { id, name, status } = experiment;
  const scheduled = pendingSchedule(experiment, nowMs);
  const base = { id, name, status, stage, deployment: null };

  if (status === 'Ended' || status === 'Deployed') {
    if (!experiment.startsAt) return { unplaced: 'No start date recorded.' };
    const ends = [experiment.endsAt, firstDeploy(experiment)].filter((t): t is number => t !== null);
    if (!ends.length) return { unplaced: 'No end date recorded.' };
    const end = Math.max(Math.min(...ends), experiment.startsAt);
    return {
      bar: {
        ...base,
        start: experiment.startsAt,
        startKind: 'actual',
        end,
        endKind: 'actual',
        note: null,
        deployment: deploymentOf(experiment, nowMs),
      },
    };
  }

  if (status === 'Running') {
    const start = experiment.startsAt ?? nowMs;
    if (scheduled.stopAt !== null) {
      return {
        bar: {
          ...base,
          start,
          startKind: 'actual',
          end: scheduled.stopAt,
          endKind: 'scheduled',
          note: 'Scheduled to stop.',
        },
      };
    }
    const projection = projections[String(id)];
    if (projection?.state === 'estimated') {
      const end = nowMs + projection.days * DAY_MS;
      return {
        bar: {
          ...base,
          start,
          startKind: 'actual',
          end,
          endKind: 'estimated',
          note: `Estimated to reach its sample in about ${projection.days} days.`,
        },
      };
    }
    if (projection?.state === 'reached') {
      return {
        bar: {
          ...base,
          start,
          startKind: 'actual',
          end: nowMs,
          endKind: 'estimated',
          note: 'Has enough data to conclude.',
        },
      };
    }
    const note =
      projection?.state === 'unreachable'
        ? 'At current traffic it will not reach its sample within a year.'
        : 'Too early to estimate an end.';
    return { bar: { ...base, start, startKind: 'actual', end: nowMs, endKind: 'open', note } };
  }

  // A draft in QA is placed as a draft: it has not started.
  if (!isPreStart(status)) return null;

  const planned = plannedDays.get(id) ?? null;
  // Held: its start time has passed and it fires as soon as the collision holding it
  // clears, so "now, probably" is the honest start rather than leaving it off entirely.
  const held = experiment.schedule?.state === 'held' ? nowMs : null;
  const start = scheduled.startAt ?? held ?? stageStart;
  if (start === null) return null;
  const startKind: EdgeKind = scheduled.startAt !== null ? 'scheduled' : 'estimated';

  if (scheduled.stopAt !== null && scheduled.stopAt > start) {
    return { bar: { ...base, start, startKind, end: scheduled.stopAt, endKind: 'scheduled', note: null } };
  }
  // Lengths come from plans people made, never from a default, so an unplanned draft is
  // listed rather than drawn at a length nobody chose.
  if (planned === null) return { unplaced: 'No planned length yet: plan its run length on its Brief tab.' };
  return {
    bar: {
      ...base,
      start,
      startKind,
      end: start + planned * DAY_MS,
      endKind: 'estimated',
      note:
        held !== null && scheduled.startAt === null
          ? `Held until a collision clears. Planned length: ${planned} days.`
          : `Planned length: ${planned} days, from this website's past traffic.`,
    },
  };
}

/** Why the chain stopped: shown to the drafts queued behind it. */
type ChainBreak = 'undated' | 'manual';

/** Why a draft in a pipeline stage has no start, by the state of the stage and pipeline. */
const waitingReason = (pipeline: TimelinePipeline, stageState: string, chainBreak: ChainBreak): string => {
  if (stageState === 'skipped') return 'Its stage was skipped.';
  if (stageState === 'running' || stageState === 'complete') return 'Did not start with its stage.';
  if (pipeline.status === 'Draft') return `Waits for "${pipeline.name}" to be started.`;
  if (pipeline.status === 'Paused') return `"${pipeline.name}" is paused.`;
  if (chainBreak === 'manual') return `Waits for an earlier stage of "${pipeline.name}" to be advanced by hand.`;
  return `Waits for an earlier stage of "${pipeline.name}" that has no end date yet.`;
};

export function buildTimeline(params: BuildTimelineParams): Timeline {
  const { experiments, pipelines, nowMs, visibleIds } = params;
  const byId = new Map(experiments.map((experiment) => [experiment.id, experiment]));
  const inPipeline = new Set<number>();
  const groups: TimelineGroup[] = [];
  const unplaced: Unplaced[] = [];

  const record = (experiment: TimelineExperiment, placement: Placement, bars: TimelineBar[]) => {
    if (!placement) return;
    if ('bar' in placement) bars.push(placement.bar);
    else unplaced.push({ id: experiment.id, name: experiment.name, reason: placement.unplaced });
  };

  for (const pipeline of pipelines) {
    if (pipeline.status === 'Cancelled') continue;
    const bars: TimelineBar[] = [];
    const stages = [...pipeline.stages].sort((a, b) => a.position - b.position);

    // Where the next pending stage would begin: the latest end of the stage before it.
    // A Draft pipeline has not started and a Paused one is not advancing, so neither
    // has a start to chain from.
    let chain: number | null = pipeline.status === 'Draft' || pipeline.status === 'Paused' ? null : nowMs;
    let chainBreak: ChainBreak = 'undated';

    for (const stage of stages) {
      const stageStart = stage.state === 'pending' ? chain : null;
      const stageBars: TimelineBar[] = [];
      let memberUnplaced = false;

      for (const { experimentId } of stage.items) {
        const experiment = byId.get(experimentId);
        if (!experiment) continue;
        inPipeline.add(experimentId);
        const placement = place({ experiment, stage: stage.name, stageStart, params });
        if (placement === null && isPreStart(experiment.status)) {
          unplaced.push({
            id: experiment.id,
            name: experiment.name,
            reason: waitingReason(pipeline, stage.state, chainBreak),
          });
          memberUnplaced = true;
          continue;
        }
        if (placement && 'unplaced' in placement) memberUnplaced = true;
        record(experiment, placement, stageBars);
      }

      bars.push(...stageBars);
      // The engine skips a skipped stage and an empty one alike (decidePendingStage), so
      // both pass the chain straight on.
      if (stage.state === 'skipped' || stage.items.length === 0) continue;

      const live = stage.state === 'pending' || stage.state === 'running';

      // A live stage with an open-ended test, or a member whose length is unknown, ends
      // at an unknown time, so nothing after it can be dated. A leftover draft in a
      // completed stage does not: the pipeline has already moved past it.
      if (live && (memberUnplaced || stageBars.some((bar) => bar.endKind === 'open'))) {
        chain = null;
        continue;
      }
      // A manual stage waits for a person, so no date after it is honest.
      if (live && stage.advanceRule === 'manual') {
        chain = null;
        chainBreak = 'manual';
        continue;
      }

      const ends = stageBars.map((bar) => bar.end);
      if (!ends.length) {
        // A completed stage with nothing to draw (its tests since deleted) passes on.
        if (stage.state !== 'complete') chain = null;
        continue;
      }
      // any-significant moves on at the first conclusion, all-concluded at the last.
      // Never earlier than now: a pending stage cannot start in the past.
      const stageEnd = stage.advanceRule === 'any-significant' ? Math.min(...ends) : Math.max(...ends);
      chain = chain === null ? null : Math.max(stageEnd, nowMs);
    }

    if (bars.length) groups.push({ key: `pipeline-${pipeline.id}`, label: pipeline.name, bars });
  }

  const rest: TimelineBar[] = [];
  for (const experiment of experiments) {
    if (inPipeline.has(experiment.id)) continue;
    record(experiment, place({ experiment, stage: null, stageStart: null, params }), rest);
  }
  rest.sort((a, b) => a.start - b.start);
  if (rest.length) groups.push({ key: 'standalone', label: 'Not in a pipeline', bars: rest });

  if (!visibleIds) return { groups, unplaced, allBars: groups.flatMap((group) => group.bars) };
  return {
    groups: groups
      .map((group) => ({ ...group, bars: group.bars.filter((bar) => visibleIds.has(bar.id)) }))
      .filter((group) => group.bars.length > 0),
    unplaced: unplaced.filter((item) => visibleIds.has(item.id)),
    // Before narrowing: the axis is sized from these, so a search changes which bars
    // are drawn but never the scale they are drawn at.
    allBars: groups.flatMap((group) => group.bars),
  };
}

/**
 * The furthest right a row draws anything: the bar's end, or its deployment's. Open
 * either way means it runs to the right edge.
 */
export const reachOf = (bar: TimelineBar): number => {
  if (bar.endKind === 'open' || bar.deployment?.ongoing) return Number.POSITIVE_INFINITY;
  return Math.max(bar.end, bar.deployment?.until ?? bar.end);
};

/** Bars that overlap [from, to] at all, so a window only draws what it can show. */
export const inWindow = (bar: TimelineBar, from: number, to: number) => bar.start <= to && reachOf(bar) >= from;

/** Months shown, or 0 for all time. A fixed range is a third behind today, two thirds ahead. */
export const TIMELINE_RANGES = [3, 6, 12, 0] as const;
export const ALL_TIME = 0;

/**
 * Horizontal scale per range, in pixels per month.
 *
 * The timeline has a fixed scale rather than a fixed width: fitting five years into the
 * panel made a two-week test a sliver a few pixels wide. At these scales the track grows
 * with the history and scrolls, and a short range still fills the panel.
 */
const PX_PER_MONTH: Record<number, number> = { 3: 280, 6: 200, 12: 140, [ALL_TIME]: 120 };

type TrackWidthParams = { from: number; to: number; months: number; viewportPx: number };

/** The track's width: the range at its scale, and never narrower than the space available. */
export const trackWidth = ({ from, to, months, viewportPx }: TrackWidthParams): number => {
  const spanMonths = (to - from) / (30 * DAY_MS);
  return Math.max(Math.round(viewportPx), Math.round(spanMonths * (PX_PER_MONTH[months] ?? 120)));
};

type TimelineWindowParams = { bars: readonly TimelineBar[]; months: number; nowMs: number };

/**
 * The span the axis covers.
 *
 * All time runs from the first test's start to the latest dated end or today, whichever
 * is later, with a sliver of padding so neither end's bar sits on the edge. Customers
 * who have tested for years would otherwise have no way to see their history at all.
 */
export function timelineWindow({ bars, months, nowMs }: TimelineWindowParams): { from: number; to: number } {
  if (months !== ALL_TIME) {
    const span = months * 30 * DAY_MS;
    return { from: nowMs - span / 3, to: nowMs + (span * 2) / 3 };
  }
  if (!bars.length) return { from: nowMs - 60 * DAY_MS, to: nowMs + 120 * DAY_MS };

  const first = Math.min(...bars.map((bar) => bar.start), nowMs);
  const last = Math.max(...bars.map((bar) => (Number.isFinite(reachOf(bar)) ? reachOf(bar) : nowMs)), nowMs);
  // Open bars fade to the right edge, so leave them somewhere to fade into.
  const pad = Math.max((last - first) * 0.03, 14 * DAY_MS);
  return { from: first - pad, to: last + pad };
}

export type AxisTick = { at: number; label: string };

/** Steps between labelled months, smallest first; the first that keeps labels readable wins. */
const TICK_STEPS = [1, 2, 3, 6, 12] as const;

/**
 * How far apart two labels must be to stay readable. The track is scrollable and sized
 * by time, so how many labels fit depends on its width in pixels, not on a fixed count.
 */
export const MIN_TICK_SPACING_PX = 80;

type AxisTicksParams = { from: number; to: number; trackPx: number };

/**
 * Month starts to label, thinned to what the track's width can hold.
 * Labels carry the year once the window spans more than one, and at a yearly step the
 * year is the whole label.
 */
export function axisTicks({ from, to, trackPx }: AxisTicksParams): AxisTick[] {
  const maxTicks = Math.max(2, Math.floor(trackPx / MIN_TICK_SPACING_PX));
  const start = new Date(from);
  start.setDate(1);
  start.setHours(0, 0, 0, 0);
  if (start.getTime() < from) start.setMonth(start.getMonth() + 1);

  const totalMonths = Math.max(1, Math.round((to - from) / (30 * DAY_MS)));
  const step = TICK_STEPS.find((candidate) => totalMonths / candidate <= maxTicks) ?? 12;
  // Aligned to the step, so yearly ticks land on January and read as years.
  while (start.getMonth() % step !== 0) start.setMonth(start.getMonth() + 1);

  const multiYear = new Date(from).getFullYear() !== new Date(to).getFullYear();
  const ticks: AxisTick[] = [];
  const cursor = new Date(start);
  while (cursor.getTime() <= to) {
    const label =
      step === 12
        ? String(cursor.getFullYear())
        : cursor.toLocaleDateString(undefined, multiYear ? { month: 'short', year: '2-digit' } : { month: 'short' });
    ticks.push({ at: cursor.getTime(), label });
    cursor.setMonth(cursor.getMonth() + step);
  }
  return ticks;
}
